import React from 'react';
import { Avatar, IconButton, Skeleton, Tooltip } from '@mui/material';

export interface UserAvatarProps {
  /**
   * Pre-computed initials to display
   * Takes priority over firstName/lastName and name
   */
  initials?: string;

  /**
   * First name - used with lastName to generate initials
   */
  firstName?: string;

  /**
   * Last name - used with firstName to generate initials
   */
  lastName?: string;

  /**
   * Full name - used to generate initials by taking first letter of each word
   */
  name?: string;

  /**
   * Visual variant of the avatar
   * - 'current': Blue background with white text (active user)
   * - 'inactive': Grey background with white text (inactive user)
   * - 'outlined': White background with blue text (default, most common)
   */
  variant?: 'current' | 'inactive' | 'outlined';

  /**
   * Avatar size
   * - number: Custom pixel size
   * - 'small': 32px (compact UI)
   * - 'medium': 48px (default, standard size)
   * - 'large': 80px (profile headers)
   */
  size?: number | 'small' | 'medium' | 'large';

  /**
   * Border configuration
   * - true: Uses default '2px solid divider' color
   * - string: Custom border CSS value
   * - false/undefined: No border
   */
  border?: boolean | string;

  /**
   * Click handler - when provided, wraps avatar in IconButton
   */
  onClick?: () => void;

  /**
   * Tooltip text - only shown when onClick is provided
   */
  tooltip?: string;

  /**
   * Size of the IconButton wrapper (if different from avatar size)
   * Only applicable when onClick is provided
   */
  buttonSize?: number;

  /**
   * Shows loading skeleton instead of avatar
   */
  loading?: boolean;

  /**
   * Icon to show when no initials are available
   */
  fallbackIcon?: React.ReactNode;

  /**
   * CSS position property
   */
  position?: 'static' | 'absolute';

  /**
   * Absolute positioning values (only used when position='absolute')
   */
  absolutePosition?: {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
  };

  /**
   * Test ID for testing
   */
  testId?: string;

  /**
   * Additional sx props for the avatar
   */
  sx?: object;
}

const sizeMap = {
  small: 32,
  medium: 48,
  large: 80,
};

const fontSizeMap = {
  small: '0.75rem',
  medium: '1rem',
  large: '2rem',
};

/**
 * Atom: Unified User Avatar Component
 *
 * A flexible avatar component for displaying user initials with various
 * styling options, interactivity, and states.
 *
 * **Features**:
 * - Multiple initials sources (pre-computed, firstName+lastName, or full name)
 * - Three visual variants (current, inactive, outlined)
 * - Size presets or custom dimensions
 * - Optional click interaction with tooltip
 * - Loading state with skeleton
 * - Fallback icon when no initials available
 * - Absolute positioning support for mobile headers
 * - Customizable borders
 *
 * @example
 * ```tsx
 * // Basic outlined avatar (most common)
 * <UserAvatar firstName="John" lastName="Doe" />
 *
 * // Current user with custom size
 * <UserAvatar name="John Doe" variant="current" size="large" />
 *
 * // Interactive avatar with tooltip
 * <UserAvatar
 *   initials="JD"
 *   onClick={() => navigate('/profile')}
 *   tooltip="Profile"
 * />
 *
 * // Absolute positioned for mobile header
 * <UserAvatar
 *   name={user?.name}
 *   onClick={handleClick}
 *   position="absolute"
 *   absolutePosition={{ top: 16, right: 16 }}
 * />
 *
 * // With loading state
 * <UserAvatar
 *   initials={user?.initials}
 *   loading={!user}
 *   fallbackIcon={<PersonIcon />}
 * />
 * ```
 */
export const UserAvatar: React.FC<UserAvatarProps> = ({
  initials: providedInitials,
  firstName,
  lastName,
  name,
  variant = 'outlined',
  size = 'medium',
  border,
  onClick,
  tooltip,
  buttonSize,
  loading = false,
  fallbackIcon,
  position = 'static',
  absolutePosition,
  testId,
  sx,
}) => {
  // Compute initials with priority: providedInitials > firstName+lastName > name
  const computedInitials = React.useMemo(() => {
    if (providedInitials) return providedInitials;
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (name) {
      return name
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase();
    }
    return '';
  }, [providedInitials, firstName, lastName, name]);

  // Resolve size
  const avatarSize = typeof size === 'number' ? size : sizeMap[size];
  const fontSize = typeof size === 'number' ? undefined : fontSizeMap[size];

  // Resolve variant styles
  const variantStyles = React.useMemo(() => {
    switch (variant) {
      case 'current':
        return {
          bgcolor: 'primary.main',
          color: 'white',
        };
      case 'inactive':
        return {
          bgcolor: 'grey.400',
          color: 'white',
        };
      case 'outlined':
      default:
        return {
          bgcolor: 'background.paper',
          color: 'primary.main',
        };
    }
  }, [variant]);

  // Resolve border
  const borderStyle = React.useMemo(() => {
    if (border === true) return '2px solid';
    if (typeof border === 'string') return border;
    return undefined;
  }, [border]);

  const borderColor = border === true ? 'divider' : undefined;

  // Loading state
  if (loading) {
    return (
      <Skeleton
        variant="circular"
        width={avatarSize}
        height={avatarSize}
        data-testid={testId ? `${testId}-loading` : 'user-avatar-loading'}
      />
    );
  }

  // Avatar element
  const avatarElement = (
    <Avatar
      sx={{
        width: avatarSize,
        height: avatarSize,
        fontSize,
        fontWeight: variant === 'outlined' ? 700 : 600,
        border: borderStyle,
        borderColor,
        ...variantStyles,
        ...sx,
      }}
      data-testid={onClick ? undefined : testId || 'user-avatar'}
    >
      {computedInitials || fallbackIcon}
    </Avatar>
  );

  // If onClick provided, wrap in IconButton
  if (onClick) {
    const button = (
      <IconButton
        onClick={onClick}
        sx={{
          width: buttonSize || avatarSize + 16,
          height: buttonSize || avatarSize + 16,
          padding: 0,
          position,
          ...(position === 'absolute' && absolutePosition),
          transition: 'transform 0.2s ease',
          '&:hover': {
            transform: 'scale(1.1)',
          },
        }}
        aria-label={tooltip || 'User profile'}
        data-testid={testId || 'user-avatar'}
      >
        {avatarElement}
      </IconButton>
    );

    if (tooltip) {
      return (
        <Tooltip title={tooltip} placement="right" arrow>
          {button}
        </Tooltip>
      );
    }

    return button;
  }

  // Non-interactive avatar with absolute positioning support
  if (position === 'absolute') {
    return (
      <div
        style={{
          position: 'absolute',
          ...absolutePosition,
        }}
      >
        {avatarElement}
      </div>
    );
  }

  return avatarElement;
};
